import React from "react";

import { Controller, useFormContext } from "react-hook-form";

import { Connection } from "src/api/connectApi/types";
import { Divider } from "src/base-components/Divider";
import { Switch } from "src/base-components/Switch";
import { EnvironmentDataSourceConfig } from "src/baseConnectionNode/EnvironmentDataSourceConfig";
import { TimeoutInput } from "src/baseConnectionNode/TimeoutInput";
import { SqlDatabaseQueryTimeoutsPill } from "src/connections/config/database/shared/SQLDatabaseQueryTimeoutsPill";
import { DatabaseConnectionForm } from "src/databaseConnectionNode/types";

type ConfigPaneProps = {
  immutable: boolean;
  connection: Connection;
};

export const ConfigPane: React.FC<ConfigPaneProps> = ({
  immutable,
  connection,
}) => {
  const formProps = useFormContext<DatabaseConnectionForm>();

  const isTimeoutActive = formProps.getValues("config.timeout.active");

  // We only specifically want to show the SQL database query timeouts pill for PostgreSQL, CockroachDB and MySQL
  const isDBStmtTimeoutUnreliable =
    connection.provider === "postgresql" ||
    connection.provider === "cockroachdb" ||
    connection.provider === "mysql";

  const LAMBDA_TIMEOUT_LIMIT = 900;

  return (
    <>
      <EnvironmentDataSourceConfig
        connection={connection}
        disabled={immutable}
      />
      <Divider spacing="my-4" />
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-inter-medium-12px text-gray-800">
            Use custom request timeout
          </h3>
          <span className="font-inter-normal-12px text-gray-500">
            Enter the maximum number of seconds before timing out the request
          </span>
        </div>
        <Controller
          control={formProps.control}
          name="config.timeout.active"
          render={(props) => (
            <Switch
              dataLoc="dbc-node-config.timeout.active"
              disabled={immutable}
              enabled={!!props.field.value}
              onChange={() => {
                props.field.onChange(!props.field.value);
              }}
            />
          )}
        />
      </div>
      {isTimeoutActive && (
        <div className="flex flex-col gap-2">
          <div className="flex w-full flex-row items-center justify-between rounded-lg bg-gray-50 px-5 py-4">
            <div className="mr-2 flex font-inter-normal-12px text-gray-700">
              Time out after (seconds)
            </div>
            <TimeoutInput
              dataLoc="dbc-node-config.timeout.timeout_seconds"
              errorMessage={`Max. supported timeout limit is ${LAMBDA_TIMEOUT_LIMIT}s`}
              formProps={formProps.register("config.timeout.timeout_seconds")}
              immutable={immutable}
              maxTimeout={LAMBDA_TIMEOUT_LIMIT}
            />
          </div>
          {isDBStmtTimeoutUnreliable && <SqlDatabaseQueryTimeoutsPill />}
        </div>
      )}
      <div className="mt-4 flex items-center justify-between">
        <div>
          <h3 className="font-inter-medium-12px text-gray-800">
            Do not error with client errors
          </h3>
          <span className="font-inter-normal-12px text-gray-500">
            In case of client errors, the flow will continue executing
          </span>
        </div>
        <Controller
          control={formProps.control}
          name="config.error.allow_client_errors"
          render={(props) => (
            <Switch
              dataLoc="dbc-node-config.error.allow_client_errors"
              disabled={immutable}
              enabled={!!props.field.value}
              onChange={() => {
                props.field.onChange(!props.field.value);
              }}
            />
          )}
        />
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div>
          <h3 className="font-inter-medium-12px text-gray-800">
            Do not error with server errors
          </h3>
          <span className="font-inter-normal-12px text-gray-500">
            In case of server errors, the flow will continue executing
          </span>
        </div>
        <Controller
          control={formProps.control}
          name="config.error.allow_server_errors"
          render={(props) => (
            <Switch
              dataLoc="dbc-node-config.error.allow_server_errors"
              disabled={immutable}
              enabled={!!props.field.value}
              onChange={() => {
                props.field.onChange(!props.field.value);
              }}
            />
          )}
        />
      </div>
    </>
  );
};
